import React, { createContext, useContext, useEffect, useState } from "react";
import api from "@/api";

const AuthCtx = createContext(null);

// Tier 1 (Feb 2026): Capture coarse geo (1.5s timeout) for auto-attendance.
const captureGeo = () => new Promise((resolve) => {
  if (!navigator.geolocation) return resolve(null);
  const t = setTimeout(() => resolve(null), 1500);
  navigator.geolocation.getCurrentPosition(
    (pos) => { clearTimeout(t); resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }); },
    () => { clearTimeout(t); resolve(null); },
    { enableHighAccuracy: false, timeout: 1500 }
  );
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("xtra_token");
    if (!token) { setLoading(false); return; }
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => localStorage.removeItem("xtra_token"))
      .finally(() => setLoading(false));
  }, []);

  // Apr 2026: Manual attendance — auto check-in on login REMOVED per user spec.
  // Field staff must tap the "Check In" card on their dashboard. Keep helper stubs for legacy callers.
  const _autoCheckIn = async () => { /* intentionally no-op — user must tap Check In */ };
  const _autoCheckOut = async () => { /* intentionally no-op — user must tap Check Out */ };

  const login = async (phone, pin) => {
    const r = await api.post("/auth/login", { phone, pin });
    localStorage.setItem("xtra_token", r.data.token);
    setUser(r.data.user);
    // Auto-attendance (silent, non-blocking)
    _autoCheckIn(r.data.user);
    return r.data.user;
  };

  // Apr 2026: Phone-only login for retailer/wholesaler outlets (no PIN).
  const phoneLogin = async (phone) => {
    const r = await api.post("/auth/phone-login", { phone });
    localStorage.setItem("xtra_token", r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };

  const logout = async () => {
    // Auto checkout BEFORE clearing token (so request still authorised)
    await _autoCheckOut(user);
    localStorage.removeItem("xtra_token");
    setUser(null);
    window.location.href = "/login";
  };

  const refresh = async () => {
    const r = await api.get("/auth/me");
    setUser(r.data);
    return r.data;
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, phoneLogin, logout, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
